// src/components/Pentests/runs/VulnScanView.js
//
// Vulnerability scan run findings (nuclei). One row per finding, ordered by
// severity, with a click-to-expand panel for full match details.

import { useMemo, useState } from 'react';
import { Bug, ChevronDown, ChevronRight, ExternalLink, Globe, Search } from 'lucide-react';

const SEVERITY_STYLE = {
  critical: 'bg-red-100 text-red-700 border-red-300',
  high: 'bg-orange-100 text-orange-700 border-orange-300',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  low: 'bg-blue-100 text-blue-700 border-blue-300',
  info: 'bg-slate-100 text-slate-700 border-slate-300',
};

const STATUS_STYLE = {
  open: 'bg-rose-100 text-rose-700 border-rose-300',
  fixed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  false_positive: 'bg-slate-100 text-slate-700 border-slate-300',
  accepted: 'bg-amber-100 text-amber-700 border-amber-300',
};

export default function VulnScanView({ run, results }) {
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(new Set());

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return results;
    return results.filter((f) =>
      [f.template_id, f.template_name, f.host, f.url, f.matched_at, f.description]
        .some((s) => String(s || '').toLowerCase().includes(q))
    );
  }, [results, filter]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      <CrawledURLsSection run={run} />

      {results.length === 0 ? (
        <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-10 text-center text-slate-500">
          <Bug className="w-6 h-6 mx-auto mb-2 opacity-60" />
          No vulnerabilities matched in this run.
        </div>
      ) : (
        <>
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by template, host, URL..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-cyber-100 border border-cyber-300/60 rounded-lg text-slate-900 placeholder-slate-500 outline-none focus:border-shinobi-green/70"
          />
        </div>
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {filtered.length === results.length
            ? `${results.length} findings`
            : `${filtered.length} of ${results.length}`}
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60">
        {filtered.map((f) => {
          const isOpen = expanded.has(f.id);
          const sev = (f.severity || 'info').toLowerCase();
          return (
            <div key={f.id} className="border-b border-cyber-300/30 last:border-b-0">
              <button
                type="button"
                onClick={() => toggle(f.id)}
                className="w-full text-left px-4 py-3 hover:bg-cyber-200/40 flex items-start gap-3"
              >
                {isOpen ? <ChevronDown className="w-4 h-4 mt-1 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 mt-1 text-slate-500 shrink-0" />}
                <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase border ${SEVERITY_STYLE[sev]} shrink-0`}>
                  {sev}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm text-shinobi-green break-all">
                    {f.template_id}
                  </div>
                  <div className="text-xs text-slate-500 break-all mt-0.5">
                    {f.matched_at}
                  </div>
                  {f.template_name && (
                    <div className="text-xs text-slate-700 mt-1">{f.template_name}</div>
                  )}
                </div>
                <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase border ${STATUS_STYLE[f.status] || STATUS_STYLE.open} shrink-0`}>
                  {f.status}
                </span>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 pl-12 space-y-3 text-sm">
                  {f.description && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Description</div>
                      <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">{f.description}</div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <KV label="Host" value={f.host} mono />
                    <KV label="Port" value={f.port} mono />
                    <KV label="URL" value={f.url} mono />
                    <KV label="Type" value={f.template_type} />
                  </div>
                  {Array.isArray(f.tags) && f.tags.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Tags</div>
                      <div className="flex flex-wrap gap-1">
                        {f.tags.map((t) => (
                          <span key={t} className="px-1.5 py-0.5 rounded text-[10px] bg-cyber-200 text-slate-700 font-mono">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {Array.isArray(f.reference) && f.reference.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">References</div>
                      <ul className="space-y-1">
                        {f.reference.slice(0, 8).map((r) => (
                          <li key={r}>
                            <a
                              href={r}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-cyan-700 hover:underline inline-flex items-center gap-1 break-all"
                            >
                              {r}
                              <ExternalLink className="w-3 h-3 shrink-0" />
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(f.extracted_results) && f.extracted_results.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Extracted</div>
                      <pre className="text-xs bg-cyber-100 border border-cyber-300/40 rounded p-2 overflow-x-auto text-slate-700 max-h-40">
                        {f.extracted_results.join('\n')}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
        </>
      )}
    </div>
  );
}

function CrawledURLsSection({ run }) {
  const [open, setOpen] = useState(false);
  const meta = run?.scan_metadata || {};
  const crawled = Array.isArray(meta.crawled_urls) ? meta.crawled_urls : [];
  const stats = meta.crawl_stats;

  if (crawled.length === 0 && !stats) return null;

  const discovered = stats?.discovered ?? crawled.length;
  const scanned = stats?.scanned ?? crawled.length;
  const capped = stats?.capped === true;
  const durationS = stats?.duration_s;

  return (
    <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-cyber-200/40 text-left"
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
        <Globe className="w-4 h-4 text-cyan-700 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-slate-800">URLs scanned</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {scanned.toLocaleString()} URLs fed to nuclei
            {discovered !== scanned ? `, ${discovered.toLocaleString()} discovered by crawler` : ''}
            {durationS != null ? ` (${durationS}s)` : ''}
          </div>
        </div>
        {capped && (
          <span className="inline-block px-2 py-0.5 rounded text-[10px] uppercase border bg-amber-100 text-amber-700 border-amber-300 shrink-0">
            Capped
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-cyber-300/30 max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-cyber-100/60 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-slate-500 uppercase tracking-wider w-16">Status</th>
                <th className="text-left px-4 py-2 font-medium text-slate-500 uppercase tracking-wider">URL</th>
                <th className="text-left px-4 py-2 font-medium text-slate-500 uppercase tracking-wider w-48">Content-Type</th>
              </tr>
            </thead>
            <tbody>
              {crawled.map((row, idx) => {
                const sc = row.status_code;
                const scClass =
                  sc >= 200 && sc < 300 ? 'text-shinobi-green' :
                  sc >= 300 && sc < 400 ? 'text-cyan-700' :
                  sc >= 400 && sc < 500 ? 'text-amber-700' :
                  sc >= 500 ? 'text-red-700' :
                  'text-slate-500';
                return (
                  <tr key={`${row.url}-${idx}`} className="border-b border-cyber-300/20 last:border-b-0 hover:bg-cyber-200/30">
                    <td className={`px-4 py-1.5 font-mono ${scClass}`}>{sc || '-'}</td>
                    <td className="px-4 py-1.5">
                      <a
                        href={row.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-slate-800 hover:text-cyan-700 break-all inline-flex items-center gap-1"
                      >
                        {row.url}
                        <ExternalLink className="w-3 h-3 opacity-60 shrink-0" />
                      </a>
                    </td>
                    <td className="px-4 py-1.5 text-slate-500 break-all">{row.content_type || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KV({ label, value, mono }) {
  if (value == null || value === '') return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-slate-800 break-all ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}
